import React, { useState, useEffect } from 'react';
import '../Styles/cart.css';
import { Link } from 'react-router-dom';
import SERVER from '../server.js';

const unactiveHomeIcon = `${SERVER}/img/home-unactive.png`;
const unactiveWishlistIcon = `${SERVER}/img/wishlist-unactive.png`;
const activeCartIcon = `${SERVER}/img/cart-active.png`;
const unactiveFriendsIcon = `${SERVER}/img/friends-unactive.png`;
const plusIcon = `${SERVER}/img/plus-icon.png`;
const minusIcon = `${SERVER}/img/minus-icon.png`;

// Notification Component
const Notification = ({ message, type, visible, onClose }) => {
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div className={`notification ${type}`}>
      <span>{message}</span>
      <button onClick={onClose} className="notification-close">×</button>
    </div>
  );
};

function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });
  const [isUpdating, setIsUpdating] = useState({});
  const [orderProgress, setOrderProgress] = useState(0);
  const [wishlistItemCount, setWishlistItemCount] = useState(0);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    const initUser = async () => {
      if (window.Telegram && window.Telegram.WebApp) {
        const tg = window.Telegram.WebApp;
        
        // Wait for Telegram WebApp to be fully ready
        tg.ready();
        
        // Add a small delay to ensure everything is loaded
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const userData = tg.initDataUnsafe?.user;
        
        // Expand the WebApp to full height
        tg.expand();
        
        // Enable closing confirmation
        tg.enableClosingConfirmation();
        
        console.log('Telegram WebApp initialized:', {
          version: tg.version,
          platform: tg.platform,
          userData: userData,
          sendDataAvailable: typeof tg.sendData === 'function',
          mainButtonAvailable: typeof tg.MainButton === 'object'
        });
        
        if (userData) {
          const userInfo = {
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            username: userData.username,
            languageCode: userData.language_code,
            isPremium: userData.is_premium
          };
          
          setUser(userInfo);
          await fetchUserData(userData.id);
        } else {
          setError('Telegram user data not available. Please open this app through Telegram.');
          setLoading(false);
        }
      } else {
        setError('This app can only be used through Telegram. Please open it via Telegram Web App.');
        setLoading(false);
      }
    };

    initUser();
  }, []);

  // Setup Telegram MainButton when cart changes
  useEffect(() => {
    if (window.Telegram && window.Telegram.WebApp && user) {
      const tg = window.Telegram.WebApp;
      
      if (cartItems.length > 0) {
        const total = calculateTotal();
        
        console.log('Setting up MainButton:', { 
          itemsCount: cartItems.length, 
          total: total,
          platform: tg.platform 
        });
        
        // Configure MainButton
        tg.MainButton.text = `CHECKOUT ($${total.toFixed(2)})`;
        tg.MainButton.color = '#fe0050';
        tg.MainButton.textColor = '#ffffff';
        tg.MainButton.show();
        tg.MainButton.enable();
        
        // Set MainButton click handler
        const handleMainButtonClick = () => {
          console.log('MainButton clicked');
          handlePayment();
        };
        
        tg.MainButton.onClick(handleMainButtonClick);
        
        // Cleanup function
        return () => {
          tg.MainButton.offClick(handleMainButtonClick);
          tg.MainButton.hide();
        };
      } else {
        // Hide MainButton when cart is empty
        tg.MainButton.hide();
      }
    }
  }, [cartItems, user]);

  const fetchUserData = async (userId) => {
    try {
      // Fetch cart items
      const cartResponse = await fetch(`${SERVER}/users/${userId}/cart`);
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        setCartItems(cartData.items || []);
      }

      // Fetch wishlist count
      const wishlistResponse = await fetch(`${SERVER}/users/${userId}/wishlist`);
      if (wishlistResponse.ok) {
        const wishlistData = await wishlistResponse.json();
        setWishlistItemCount(Array.isArray(wishlistData) ? wishlistData.length : 0);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateShipping = () => {
    return 0; // Free shipping always
  };

  const calculateTax = () => {
    return 0; // No tax/commission
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateShipping() + calculateTax();
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    setIsUpdating(prev => ({ ...prev, [productId]: true }));
    
    try {
      const response = await fetch(`${SERVER}/users/${user.id}/cart/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity: newQuantity })
      });

      if (!response.ok) throw new Error('Failed to update quantity');
      
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.product_id === productId
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
      
      showNotification('Quantity updated successfully!', 'success');
    } catch (err) {
      setError(err.message);
      showNotification('Failed to update quantity', 'error');
    } finally {
      setIsUpdating(prev => ({ ...prev, [productId]: false }));
    }
  };

  const removeFromCart = async (productId) => {
    setIsUpdating(prev => ({ ...prev, [productId]: true }));
    
    try {
      const response = await fetch(`${SERVER}/users/${user.id}/cart/${productId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to remove item');
      
      setCartItems(prevItems => prevItems.filter(item => item.product_id !== productId));
      showNotification('Item removed from cart', 'success');
    } catch (err) {
      setError(err.message);
      showNotification('Failed to remove item', 'error');
    } finally {
      setIsUpdating(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handlePayment = () => {
    console.log('handlePayment called');
    
    if (!cartItems.length) {
      showNotification('Your cart is empty', 'error');
      return;
    }
    
    // Check if Telegram WebApp is available
    if (!window.Telegram || !window.Telegram.WebApp) {
      showNotification('This app must be opened through Telegram', 'error');
      return;
    }
    
    const tg = window.Telegram.WebApp;
    console.log('Telegram WebApp info:', {
      version: tg.version,
      platform: tg.platform,
      isVersionAtLeast: tg.isVersionAtLeast ? tg.isVersionAtLeast('6.1') : 'unknown'
    });
    
    setOrderProgress(1);
    showNotification('Processing your order...', 'success');
    
    try {
      // Prepare order data in the format expected by the bot
      const orderData = {
        type: 'order',
        products: cartItems.map(item => ({
          id: item.product_id,
          quantity: item.quantity
        })),
        total: calculateTotal()
      };
      
      console.log('Order data prepared:', orderData);
      
      // Send data to bot
      setTimeout(() => {
        setOrderProgress(2);
        showNotification('Sending order to bot...', 'success');
        
        const dataString = JSON.stringify(orderData);
        console.log('Attempting to send data:', dataString);
        
        // Try to send data via Telegram Web App API
        try {
          if (typeof tg.sendData === 'function') {
            console.log('sendData function is available, sending data...');
            
            // Send the data
            tg.sendData(dataString);
            
            console.log('sendData called successfully');
            
            // Show success message
            setTimeout(() => {
              setOrderProgress(0);
              showNotification('✅ Order sent to bot! Check your chat.', 'success');
              tg.disableClosingConfirmation();
            }, 800);
            
          } else {
            console.error('sendData function is not available');
            setOrderProgress(0);
            
            // Show more detailed error message
            const errorMsg = `❌ Web App API not supported.\nPlatform: ${tg.platform}\nVersion: ${tg.version}`;
            showNotification(errorMsg, 'error');
          }
        } catch (sendError) {
          console.error('Error calling sendData:', sendError);
          setOrderProgress(0);
          showNotification('❌ Failed to send order. Error: ' + sendError.message, 'error');
        }
        
      }, 800);
      
    } catch (error) {
      console.error('Error preparing order data:', error);
      showNotification('Failed to process order', 'error');
      setOrderProgress(0);
    }
  };

  const clearCart = async () => {
    if (!cartItems.length) return;
    
    try {
      // Remove all items from cart
      await Promise.all(
        cartItems.map(item => 
          fetch(`${SERVER}/users/${user.id}/cart/${item.product_id}`, {
            method: 'DELETE'
          })
        )
      );
      
      setCartItems([]);
      showNotification('Cart cleared successfully', 'success');
    } catch (err) {
      showNotification('Failed to clear cart', 'error');
    }
  };

  if (loading) {
    return (
      <div className="cart-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <div>Loading your cart...</div>
          <small>Preparing your items</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cart-container">
        <div className="error">
          <div className="error-icon">🛒</div>
          <strong>Unable to load your cart</strong>
          <br />
          {error}
          <br />
          <div className="error-actions">
            <button onClick={() => window.location.reload()} className="retry-btn">
              Try Again
            </button>
            <Link to="/" className="continue-shopping-btn">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <Notification
        message={notification.message}
        type={notification.type}
        visible={notification.visible}
        onClose={closeNotification}
      />

      <div className="cart-content">
        <div className="cart-header">
          <h1 className="cart-title">YOUR CART</h1>
          {cartItems.length > 0 && (
            <div className="cart-actions">
              <span className="item-count">{cartItems.length} item{cartItems.length !== 1 ? 's' : ''}</span>
              <button onClick={clearCart} className="clear-cart-btn">
                Clear All
              </button>
            </div>
          )}
        </div>
        
        {cartItems.length === 0 ? (
          <div className="empty-cart">
            <div className="empty-cart-icon">🛒</div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added any items to your cart yet.</p>
            <div className="empty-cart-actions">
              <Link to="/" className="btn-shop">
                Start Shopping
              </Link>
              <Link to="/Wishlist" className="btn-wishlist">
                Check Wishlist
              </Link>
            </div>
            <div className="cart-benefits">

              <div className="benefit">
                <span className="benefit-icon">🔒</span>
                <span>Secure checkout</span>
              </div>
              <div className="benefit">
                <span className="benefit-icon">📞</span>
                <span>24/7 customer support</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="cart-items">
              {cartItems.map((item) => (
                <div key={item.product_id} className="cart-item">
                  <div className="item-image-container">
                    <img 
                      src={item.photo_path ? `${SERVER}/img/${item.photo_path}` : `${SERVER}/img/error.png`}
                      alt={item.name} 
                      className="item-image" 
                    />
                  </div>
                  <div className="item-details">
                    <div className="item-header">
                      <h3 className="item-name">{item.name}</h3>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="remove-item-btn"
                        disabled={isUpdating[item.product_id]}
                        title="Remove item"
                      >
                        ×
                      </button>
                    </div>
                    <p className="item-description">
                      {item.description}
                    </p>
                    <div className="item-price-quantity">
                      <div className="quantity-controls">
                        <button 
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          disabled={isUpdating[item.product_id] || item.quantity <= 1}
                          title="Decrease quantity"
                        >
                          <img src={minusIcon} alt="Decrease" className="quantity-icon" />
                        </button>
                        <span className="item-quantity">
                          {isUpdating[item.product_id] ? '...' : item.quantity}
                        </span>
                        <button 
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          disabled={isUpdating[item.product_id]}
                          title="Increase quantity"
                        >
                          <img src={plusIcon} alt="Increase" className="quantity-icon" />
                        </button>
                      </div>
                      <div className="price-info">
                        <span className="unit-price">${item.price.toFixed(2)} each</span>
                        <span className="item-total">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="cart-summary">
              <div className="summary-header">
                <h3>Order Summary</h3>
              </div>
              
              <div className="summary-details">
                <div className="summary-line">
                  <span>Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="summary-line total-line">
                  <span>Total</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              <div className="security-badges">
                <span className="security-badge">💳 Secure Payment</span>
                <span className="security-badge">🛡️ Purchase Protection</span>
              </div>
              
              {/* Show button only when not in Telegram WebApp or as fallback */}
              {(!window.Telegram || !window.Telegram.WebApp) && (
                <button 
                  className={`process-payment-btn ${orderProgress > 0 ? 'processing' : ''}`}
                  onClick={handlePayment}
                  disabled={orderProgress > 0}
                >
                  {orderProgress === 0 && 'CHECKOUT SECURELY'}
                  {orderProgress === 1 && 'PROCESSING...'}
                  {orderProgress === 2 && 'REDIRECTING...'}
                </button>
              )}
              
              {/* Info text when using Telegram MainButton */}
              {(window.Telegram && window.Telegram.WebApp) && (
                <div className="telegram-checkout-info">
                  <p>💬 Use the <strong>CHECKOUT</strong> button below to place your order</p>
                  <small>
                    Platform: {window.Telegram.WebApp.platform || 'unknown'} | 
                    Version: {window.Telegram.WebApp.version || 'unknown'}
                  </small>
                </div>
              )}

            </div>
          </>
        )}
      </div>

      <nav className="bottom-nav">
        <Link to="/" className="nav-btn">
          <img src={unactiveHomeIcon} alt="Home" className="nav-icon" />
          <span>Home</span>
        </Link>
        <Link to="/Wishlist" className="nav-btn">
          <img src={unactiveWishlistIcon} alt="Wishlist" className="nav-icon" />
          <span>Wishes</span>
          {wishlistItemCount > 0 && (
            <span className="nav-badge">{wishlistItemCount}</span>
          )}
        </Link>
        <Link to="/Cart" className="nav-btn active">
          <img src={activeCartIcon} alt="Cart" className="nav-icon" />
          <span>Cart</span>
          {cartItems.length > 0 && (
            <span className="nav-badge">{cartItems.length}</span>
          )}
        </Link>
        <Link to="/Friends" className="nav-btn">
          <img src={unactiveFriendsIcon} alt="Friends" className="nav-icon" />
          <span>Friends</span>
        </Link>
      </nav>
    </div>
  );
}

export default Cart;